/**
 * Clone a past activity into a new draft.
 * Copies only reusable content — never bookings, history, or analytics.
 * Also duplicates media_assets rows so images appear in the new draft.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ProducerEvent } from '@/types/events';

/** Fields to copy from the original event */
const CLONE_FIELDS = [
  'producer_id',
  'title',
  'event_type',
  'description',
  'use_producer_address',
  'custom_address',
  'custom_lat',
  'custom_lng',
  'price_ore',
  'currency',
  'max_spots',
  'booking_deadline_hours',
  'is_free',
  'booking_mode',
  'audience',
  'learning_points',
  'includes_farm_sale',
  'includes_farm_sale_alcohol',
  'timing_mode',
  'outing_tags',
  'short_description',
] as const;

/** Media asset fields safe to copy (exclude id, created_at, draft_session_id) */
const MEDIA_COPY_FIELDS = [
  'owner_type',
  'kind',
  'bucket',
  'path_320',
  'path_640',
  'path_1280',
  'mime',
  'width',
  'height',
  'size_bytes',
  'alt_text',
  'sort_order',
  'created_by',
  'season_key',
] as const;

export function useCloneActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sourceEvent: ProducerEvent & Record<string, unknown>) => {
      // Use a future placeholder so the clone is never treated as past.
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(12, 0, 0, 0);
      const placeholder = tomorrow.toISOString();

      // Build insert payload from allowed fields only
      const payload: Record<string, unknown> = {
        status: 'draft',
        start_date: placeholder,
        end_date: placeholder,
        recurrence_rule: null,
        is_recurring: false,
      };

      for (const key of CLONE_FIELDS) {
        if (key in sourceEvent && sourceEvent[key] !== undefined) {
          payload[key] = sourceEvent[key];
        }
      }

      // Keep original title — no "(kopia)" suffix
      payload.title = sourceEvent.title;

      const { data, error } = await supabase
        .from('producer_events')
        .insert(payload as any)
        .select()
        .single();

      if (error) throw error;
      const newEvent = data as ProducerEvent;

      // Duplicate media_assets from source → new event (non-fatal)
      try {
        const { data: sourceMedia } = await supabase
          .from('media_assets')
          .select('*')
          .eq('owner_type', 'event')
          .eq('owner_id', sourceEvent.id);

        if (sourceMedia && sourceMedia.length > 0) {
          const mediaRows = sourceMedia.map((asset: any) => {
            const row: Record<string, unknown> = { owner_id: newEvent.id };
            for (const field of MEDIA_COPY_FIELDS) {
              if (field in asset) row[field] = asset[field];
            }
            return row;
          });
          await supabase.from('media_assets').insert(mediaRows as any);
        }
      } catch (mediaErr) {
        console.warn('[clone-activity] media copy failed (non-fatal):', mediaErr);
      }

      return newEvent;
    },
    onSuccess: (newEvent) => {
      queryClient.invalidateQueries({ queryKey: ['producer-events', newEvent.producer_id] });
      queryClient.invalidateQueries({ queryKey: ['media-assets', 'event', newEvent.id] });
    },
  });
}
